import { createSelector, createSlice, PayloadAction } from "@reduxjs/toolkit";
import type {
  IGetCeilingFreeTileBoundingBoxesArgs,
  IRoomCeilingFreeTile,
} from "@multiconsult-group/multiplacer-core";
import { getCeilingFreeTileBoundingBoxes } from "@multiconsult-group/multiplacer-core";
import { IRootState } from "../../app/store";
import { selectActivePartialMcbimSpace } from "../../McBim/mcbimSelectors";
import { selectHttpMcBimItemsInSelectedRoom } from "../singleRoomSelectors";

const LS_KEYS = {
  showOnlyLockedRooms: "ceiling.showOnlyLockedRooms",
} as const;

const loadBoolean = (key: string, fallback: boolean): boolean => {
  const value = localStorage.getItem(key);
  if (value == null) return fallback;
  return value === "true";
};

export interface ICeilingSliceState {
  showOnlyLockedRooms: boolean;
  selectedSimilarRoomWithDevicesNumber: string | null;
  selectedSimilarRoomWithIfcDesignNumber: string | null;
  showFreeTilesInSelectedRoom: boolean;
}

const initialState: ICeilingSliceState = {
  showOnlyLockedRooms: loadBoolean(LS_KEYS.showOnlyLockedRooms, false),
  selectedSimilarRoomWithDevicesNumber: null,
  selectedSimilarRoomWithIfcDesignNumber: null,
  showFreeTilesInSelectedRoom: false,
};

export const ceilingSlice = createSlice({
  name: "ceiling",
  initialState,
  reducers: {
    setCeilingShowOnlyLockedRooms: (state, action: PayloadAction<boolean>) => {
      localStorage.setItem(LS_KEYS.showOnlyLockedRooms, String(action.payload));
      state.showOnlyLockedRooms = action.payload;
    },
    setCeilingSelectedSimilarRoomWithDevicesNumber: (
      state,
      action: PayloadAction<string | null>
    ) => {
      state.selectedSimilarRoomWithDevicesNumber = action.payload;
    },
    setCeilingSelectedSimilarRoomWithIfcDesignNumber: (
      state,
      action: PayloadAction<string | null>
    ) => {
      state.selectedSimilarRoomWithIfcDesignNumber = action.payload;
    },
    setShowFreeTilesInSelectedRoom: (state, action: PayloadAction<boolean>) => {
      state.showFreeTilesInSelectedRoom = action.payload;
    },
  },
});

export const {
  setCeilingSelectedSimilarRoomWithDevicesNumber,
  setCeilingSelectedSimilarRoomWithIfcDesignNumber,
  setCeilingShowOnlyLockedRooms,
  setShowFreeTilesInSelectedRoom,
} = ceilingSlice.actions;

export const selectCeilingShowOnlyLockedRooms = (state: IRootState): boolean =>
  state.ceiling.showOnlyLockedRooms;

export const selectCeilingSelectedSimilarRoomWithDevicesNumber = (
  state: IRootState
): string | null => state.ceiling.selectedSimilarRoomWithDevicesNumber;

export const selectCeilingSelectedSimilarRoomWithIfcDesignNumber = (
  state: IRootState
): string | null => state.ceiling.selectedSimilarRoomWithIfcDesignNumber;

export const selectShowFreeTilesInSelectedRoom = (state: IRootState): boolean =>
  state.ceiling.showFreeTilesInSelectedRoom;

export const selectSelectedRoomCeilingFreeTilesInput = createSelector(
  [selectActivePartialMcbimSpace, selectHttpMcBimItemsInSelectedRoom],
  (activeSpace, mcbimItems): IGetCeilingFreeTileBoundingBoxesArgs | null => {
    if (!activeSpace) return null;

    return {
      ceilingGrids: activeSpace.ceilingGrids ?? [],
      mcbimItems,
    };
  }
);

export const selectCalculatedSelectedRoomCeilingFreeTiles = createSelector(
  [selectSelectedRoomCeilingFreeTilesInput],
  (input): IRoomCeilingFreeTile[] | null => {
    if (!input) return null;
    return getCeilingFreeTileBoundingBoxes(input);
  }
);
